import { useCallback, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import axiosInstance from "../../../../../services/AxiosInstance";
import Api from "../../../../../constants/Api";
import { useBranch } from "../../../../../context/BranchContext";
import { ApiError } from "../../../Common/ApiError";
import { offerToOpenCash } from "../Cash/offerToOpenCash";
import EmployeeConsumptionPriceType, { resolveItemPriceType } from "./EmployeeConsumptionPriceType";

/**
 * Item novo da lista: um produto por linha, começando em uma unidade.
 * O key é só da tela, para o React não trocar as linhas de lugar ao remover uma do meio.
 */
let itemKeySequence = 0;
export const newConsumptionItem = () => ({ key: ++itemKeySequence, product: null, quantity: '1' });

/** Acerto escolhido no lançamento: descontar do líquido do colaborador ou receber agora. */
export const EmployeeConsumptionSettlement = {
    DISCOUNT: 'DISCOUNT',
    PAY_NOW: 'PAY_NOW'
};

/**
 * Estado, validação e envio do lançamento de consumo do colaborador.
 *
 * Fica separado dos campos (EmployeeConsumptionForm) para a página da rota /new e o modal da
 * tela de Caixas usarem exatamente a mesma regra de formulário; cada um só decide a moldura e
 * o que fazer depois de salvar. Preço, estoque e caixa continuam decididos pelo backend.
 *
 * @param {function} onSaved - Chamado com o consumo gravado, depois de o formulário ser limpo.
 * @param {object} initialEmployee - Opcional: colaborador já escolhido ({ value, label }).
 * @param {function} onCashPromptOpen - Opcional: chamado antes da pergunta de abrir caixa. O
 *        modal usa para se esconder, senão o alerta fica atrás dele.
 * @param {function} onCashPromptDeclined - Opcional: chamado quando a pessoa não abre o caixa
 *        (ou a abertura falha), para o modal voltar com o que já estava preenchido.
 */
const useEmployeeConsumptionForm = ({
    onSaved, initialEmployee = null, onCashPromptOpen, onCashPromptDeclined
} = {}) => {

    const { t } = useTranslation();
    const { selectedBranch } = useBranch();

    const branchFromContext = selectedBranch ? { value: selectedBranch.id, label: selectedBranch.name } : null;

    // Descontar é o padrão: é o caso que motivou o recurso e o que a recepção mais lança.
    const buildInitialValues = () => ({
        branch: branchFromContext,
        employee: initialEmployee,
        // Vários produtos num lançamento só: o colaborador costuma pegar mais de uma coisa, e
        // abrir o formulário uma vez por item era o que fazia a recepção deixar de lançar.
        items: [newConsumptionItem()],
        priceType: EmployeeConsumptionPriceType.NORMAL,
        settlement: EmployeeConsumptionSettlement.DISCOUNT,
        paymentMethod: null,
        description: ''
    });

    const validationSchema = Yup.object().shape({
        branch: Yup.object().nullable().required(t('validation.required.branch')),
        employee: Yup.object().nullable().required(t('validation.required.employee')),
        items: Yup.array()
            .min(1, t('cash_flow.consumption_items_required'))
            .of(Yup.object().shape({
                product: Yup.object().nullable().required(t('validation.required.product')),
                quantity: Yup.number()
                    .typeError(t('validation.required.qty'))
                    .positive(t('validation.required.qty'))
                    .required(t('validation.required.qty'))
            })),
        // Forma de pagamento só é exigida quando o colaborador vai pagar na hora.
        paymentMethod: Yup.object().nullable().when('settlement', {
            is: EmployeeConsumptionSettlement.PAY_NOW,
            then: schema => schema.required(t('validation.required.payment_method'))
        }),
        description: Yup.string().max(255, t('validation.max_length', { max: 255 }))
    });

    const formik = useFormik({
        initialValues: buildInitialValues(),
        validationSchema,
        // Só valida ao salvar: no celular, erro aparecendo a cada toque atrapalha mais do que ajuda.
        validateOnChange: false,
        validateOnBlur: false,
        onSubmit: (values, helpers) => {
            const payload = {
                branch: { id: values.branch.value },
                employee: { id: values.employee.value },
                // Cada item vai com o tipo de preço que a tela mostrou: no lançamento pelo custo,
                // o produto sem custo cadastrado segue pelo preço normal sem mudar os outros.
                items: values.items.map(item => ({
                    product: { id: item.product.value },
                    quantity: Number(item.quantity),
                    priceType: resolveItemPriceType(values.priceType, item.product)
                })),
                priceType: values.priceType,
                description: values.description.trim() || null,
                // Forma de pagamento preenchida é o que faz o backend lançar no caixa na hora;
                // vazia, o consumo fica para descontar da comissão.
                paymentMethod: values.settlement === EmployeeConsumptionSettlement.PAY_NOW
                    ? values.paymentMethod.value
                    : null
            };

            return send(payload).finally(() => helpers.setSubmitting(false));
        }
    });

    /**
     * Grava todos os itens de uma vez e, dando certo, limpa o formulário e avisa quem abriu.
     * O backend grava tudo ou nada: se um produto não tiver estoque, nenhum item entra.
     */
    const send = (payload) => axiosInstance.post(`${Api.cash_flow.employee_consumption}/batch`, payload)
        .then(({ data }) => {
            reset();
            onSaved?.(data);
        })
        .catch(error => {
            // Pago em dinheiro sem caixa aberto: mesma saída da venda, que pergunta se quer abrir
            // o caixa com saldo zero e segue com o lançamento. O caixa aberto é do usuário logado,
            // que é para onde o backend manda o dinheiro. Os demais erros (estoque sem saldo,
            // custo não cadastrado) vêm com a mensagem do backend, que já nomeia produto e filial.
            const handled = offerToOpenCash({
                error,
                branchId: payload.branch?.id,
                question: t('cash_flow.open_cash_before_consumption'),
                openingNote: t('cash_flow.automatic_opening_for_consumption'),
                onBeforeAsk: onCashPromptOpen,
                onOpened: () => {
                    // Mantém o botão travado enquanto o lançamento é repetido.
                    formik.setSubmitting(true);
                    return send(payload).finally(() => formik.setSubmitting(false));
                },
                onDeclined: onCashPromptDeclined
            });
            if (!handled) {
                ApiError(error);
            }
        });

    /**
     * Limpa para o próximo lançamento mantendo a filial do topo: é comum lançar vários
     * consumos seguidos na mesma unidade.
     */
    const reset = useCallback(() => {
        formik.resetForm({ values: buildInitialValues() });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedBranch, initialEmployee]);

    // Trocar a filial no topo troca a do formulário, e o colaborador escolhido deixa de valer:
    // ele pode não trabalhar na unidade nova.
    useEffect(() => {
        formik.setFieldValue('branch', branchFromContext);
        formik.setFieldValue('employee', initialEmployee);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedBranch]);

    return { formik, reset, isBranchFixed: Boolean(selectedBranch) };
};

export default useEmployeeConsumptionForm;
